import { FolderRegular, HomeRegular, ShareRegular } from '@fluentui/react-icons';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Drive, DriveType } from '@mnspro-cloud/services';
import styles from './FilesDriveList.module.css';
import { sortDrivesForDisplay } from './Files.utils';

interface FilesDriveListProps {
  drives: Drive[];
  onDriveClick: (driveItem: Drive) => void;
  selectedDrive: Drive | null;
}

const getIcon = (drive: Drive) => {
  switch (drive.type) {
    case DriveType.Personal:
      return <HomeRegular fontSize={20} />;
    case DriveType.SchoolWide:
      return <ShareRegular fontSize={20} />;
    default:
      return <FolderRegular fontSize={20} />;
  }
};

/**
 * Component for displaying a list of drives the user has access to.
 */
export const FilesDriveList: React.FC<FilesDriveListProps> = ({
  drives,
  onDriveClick,
  selectedDrive,
}) => {
  const { t } = useTranslation();
  const sortedDrives = sortDrivesForDisplay(drives);

  return (
    <ul
      className={styles.filesDriveList}
      aria-label={t('files_drivestree_label', {
        defaultValue:
          'Ordner / Gruppenauswahl zum Filtern der Dateien auf den ausgewählten Speicherort',
      })}
    >
      {sortedDrives.map((drive) => {
        let label = '';

        switch (drive.type) {
          case DriveType.Personal:
            label = t('files_me_drive', { defaultValue: 'Meine Dokumente' });
            break;
          case DriveType.Group:
            label = drive.label || '';
            break;
          case DriveType.SchoolWide:
            label = t('files_school_wide_drive', {
              defaultValue: 'Schulweite Freigabe',
            });
            break;
        }

        const isSelected = selectedDrive !== null && drive.id === selectedDrive.id;

        return (
          <li key={drive.id}>
            <button
              type="button"
              className={cn(styles.driveItem, {
                [styles.driveItemSelected]: isSelected,
              })}
              aria-current={isSelected ? 'page' : undefined}
              onClick={() => onDriveClick(drive)}
            >
              <span className={styles.driveIcon}>{getIcon(drive)}</span>
              <span className={styles.driveLabel}>{label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};
